/* Shared design tokens for every Scriptorium primitive. Kept in one
 * module so palette / grain / font decisions live in exactly one place
 * and every sibling component (rail, bookmark, slider, bookshelf,
 * unread stack, typewriter, pro-writer gateway) imports from here.
 */
import { useEffect } from 'react';

/* Colour palette — cream paper / cocoa ink / orange accent.
 * 2026-02 — Re-tuned to match the Crown & Rose design-system tokens
 * (`--cr-*`) defined in `/api/theme/crown-rose-theme.css`. Property
 * names intentionally kept stable so sibling components don't change. */
export const P = {
  paper:       '#FCFBFA',   // --cr-surface
  paperDeep:   '#F5EFEA',   // --cr-surface-secondary
  paperBorder: '#EBE1DA',   // --cr-border
  ink:         '#2B2321',   // --cr-on-surface
  inkSoft:     '#423633',   // --cr-on-surface-secondary
  oxblood:     '#E57A1C',   // --cr-brand (warm orange; replaces oxblood)
  oxbloodHi:   '#F7A04B',   // --cr-brand-primary (lighter hover state)
  verdant:     '#3C7A5B',   // --cr-success
  gold:        '#FBB040',   // --cr-warning / highlight
  goldDeep:    '#E57A1C',   // --cr-brand (same as oxblood)
  leather:     '#2B2321',   // --cr-surface-inverse
  leatherHi:   '#423633',   // --cr-on-surface-secondary
};

/* CSS-only SVG noise. Rendered as a background-image so it composites
 * cheaply and needs no external asset.
 * 2026-10 — feColorMatrix alpha dropped 0.18 → 0.04 (design-audit:
 * "heavy paper grain ... looks like noise/dirt on a high-res screen").
 * Combined with lower per-component overlay opacities, this should
 * read as a faint tactile hint rather than a blanket of static. */
export const GRAIN_URL =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.14 0 0 0 0 0.13 0 0 0 0 0.11 0 0 0 0 0.04 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")";

/* Common font stacks — Fraunces (editorial headings) + DM Sans (body/UI).
 * 2026-02 — Updated to match the Crown & Rose design-system.
 * Both fonts are pre-loaded by `/api/theme/crown-rose-theme.css` in
 * `public/index.html`, so no extra @import is needed here. */
export const FONTS = {
  display: "'Fraunces','Cormorant Garamond','Georgia',serif",  // editorial display headings
  body:    "'DM Sans','-apple-system','Segoe UI',sans-serif",  // UI / body text
  serif:   "'Fraunces','Georgia',serif",                        // alias for prose paragraphs
  type:    "'Special Elite','Courier New',monospace",           // typewriter accents
};

/* Ensures Fraunces + DM Sans are loaded exactly once.
 * Both fonts are also pre-loaded by the theme link in public/index.html,
 * so this hook is a safety net for any context where that link is absent. */
export function useScriptoriumFonts() {
  useEffect(() => {
    if (document.querySelector('link[data-scriptorium-fonts]')) return;
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300..900;1,9..144,300..900&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&family=Special+Elite&display=swap';
    link.setAttribute('data-scriptorium-fonts', 'true');
    document.head.appendChild(link);
  }, []);
}
